import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import {
  AuthenticationService,
  ScopeAuthorizationService,
  TenantRouterService,
} from '@mds/ngx-auth';
import { LocalizationService } from '@mds/ngx-core';
import {
  MDS_LAYOUT_NAVIGATION_ITEMS,
  NavigationChildItem,
  NavigationItem,
  NavigationWithChildrenItem,
} from '@mds/ngx-layout';
import {
  combineLatest,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import {
  AppNavigationChildItem,
  AppNavigationItem,
  AppNavigationWithChildrenItem,
} from '../models/app-navigation-item.model';
import { untranslatedNavigationItems } from '../models/navigation-items.model';

export function provideNavigationItems(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: MDS_LAYOUT_NAVIGATION_ITEMS,
      useFactory: navigationGroupsFactory,
      deps: [
        LocalizationService,
        TranslocoService,
        AuthenticationService,
        ScopeAuthorizationService,
        TenantRouterService,
      ],
    },
  ]);
}

export function navigationGroupsFactory(
  localizationService: LocalizationService,
  translateService: TranslocoService,
  authService: AuthenticationService,
  scopeAuthorizationService: ScopeAuthorizationService,
  tenantRouterService: TenantRouterService,
): Signal<NavigationItem[]> {
  return toSignal(
    combineLatest([
      localizationService.translationsLoaded$.pipe(filter(Boolean)),
      translateService.langChanges$,
      authService.isAuthenticated$.pipe(filter(Boolean)),
    ]).pipe(
      switchMap(() => {
        return forkJoin(
          untranslatedNavigationItems.map((item) =>
            toNavigationItem(
              tenantRouterService,
              translateService,
              scopeAuthorizationService,
              item,
            ),
          ),
        );
      }),
      map((items) => items.filter((i): i is NavigationItem => i !== null)),
    ),
    { initialValue: [] },
  );
}

function toNavigationItem(
  tenantRouterService: TenantRouterService,
  translateService: TranslocoService,
  scopeAuthorizationService: ScopeAuthorizationService,
  item: AppNavigationItem,
): Observable<NavigationItem | null> {
  return scopeAuthorizationService.hasScopes(item.requiredScopes ?? []).pipe(
    switchMap((visible) => {
      if (!visible) {
        return of(null);
      }

      if (isNavigationWithChildren(item)) {
        return toAuthorizedChildren(
          tenantRouterService,
          translateService,
          scopeAuthorizationService,
          item.children,
        ).pipe(
          map((children) =>
            children.length > 0
              ? toWithChildren(translateService, item, children)
              : null,
          ),
        );
      }

      return of(toWithoutChildren(tenantRouterService, translateService, item));
    }),
  );
}

function toAuthorizedChildren(
  tenantRouterService: TenantRouterService,
  translateService: TranslocoService,
  scopeAuthorizationService: ScopeAuthorizationService,
  children: AppNavigationChildItem[],
): Observable<NavigationChildItem[]> {
  if (children.length === 0) {
    return of([]);
  }

  return forkJoin(
    children.map((child) =>
      scopeAuthorizationService
        .hasScopes(child.requiredScopes ?? [])
        .pipe(
          map((visible) =>
            visible
              ? toWithoutChildren(tenantRouterService, translateService, child)
              : null,
          ),
        ),
    ),
  ).pipe(
    map((children) =>
      children.filter((child): child is NavigationChildItem => child !== null),
    ),
  );
}

function isNavigationWithChildren(
  item: AppNavigationItem,
): item is AppNavigationWithChildrenItem {
  return 'children' in item;
}

function toWithChildren(
  translateService: TranslocoService,
  item: AppNavigationWithChildrenItem,
  children: NavigationChildItem[],
): NavigationWithChildrenItem {
  return {
    icon: item.icon,
    label: translateService.translate(item.label),
    children,
  };
}

function toWithoutChildren(
  tenantRouterService: TenantRouterService,
  translateService: TranslocoService,
  item: AppNavigationChildItem,
): NavigationChildItem {
  const tenantLink = item.link
    ? tenantRouterService.addTenant(item.link)
    : undefined;

  return {
    ...item,
    label: translateService.translate(item.label),
    link: tenantLink ?? '',
  };
}
